import {
  isDatePoll,
  isManyOfManyPoll,
  isManyOfManyPollResponse,
  isOneOfManyPoll,
  isOneOfManyPollResponse,
} from '@tunasong/schemas'
import type { Poll, PollResponse } from '@tunasong/schemas'
import invariant from 'tiny-invariant'

/**
 * Answers save as they are picked, so a stored response can be a work in progress. Complete is derived, never
 * stored: it depends on the poll's quota, which the owner can change, and a stored flag would then be wrong on
 * every response.
 *
 * - One-of-many: a choice was made.
 * - Many-of-many with a quota: exactly the quota is picked (more is prevented in the UI).
 * - Many-of-many without a quota: at least one pick. Ticked-then-unticked says nothing.
 * - Date polls: always. Their "done" is `getWaitingOn`, and an all-"no" respondent has answered.
 */
export const isCompleteResponse = (response: PollResponse, poll: Poll) => {
  if (isDatePoll(poll)) {
    return true
  }
  if (isOneOfManyPollResponse(response)) {
    return typeof response.choice === 'number'
  }
  if (!isManyOfManyPollResponse(response)) {
    return false
  }
  const picked = Object.values(response.choices ?? {}).filter(Boolean).length
  return poll.responseQuota ? picked === poll.responseQuota : picked > 0
}

/** Get the percentage progress of a poll option identified by idx, over complete answers only */
export const getProgress = (poll: Poll, allResponses: PollResponse[], idx: number) => {
  const complete = allResponses.filter(response => isCompleteResponse(response, poll))
  let responsesForOption = 0
  const numResponses = complete.length

  if (isManyOfManyPoll(poll)) {
    const responses = complete.map(r => {
      invariant(isManyOfManyPollResponse(r), `Response must be a ManyOfManyPollResponse`)
      return r.choices
    })
    if (responses.length === 0) {
      return 0
    }
    responsesForOption = responses.filter(r => typeof r === 'object' && r[idx]).length
  } else if (isOneOfManyPoll(poll)) {
    responsesForOption = complete.filter(r => {
      invariant(isOneOfManyPollResponse(r), `Response must be a OneOfManyPollResponse`)
      return r.choice === idx
    }).length
  }

  /**
   * The many-of-many branch returns early at zero responses; the one-of-many branch fell
   * through to this division and rendered `NaN%`. Guard both.
   */
  if (numResponses === 0) {
    return 0
  }

  return (responsesForOption / numResponses) * 100
}
